import React, { useMemo, useState } from "react";

function parseNum(v) {
  const s = String(v ?? "")
    .trim()
    .replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function clamp(n, min, max) {
  if (!Number.isFinite(n)) return min;
  return Math.min(Math.max(n, min), max);
}

function formatEUR(n) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(n);
}

function calcMonthlyPayment(principal, annualRatePct, months) {
  const P = principal;
  const n = months;
  const r = annualRatePct / 100 / 12;

  if (P <= 0 || n <= 0) return 0;
  if (r === 0) return P / n;

  const denom = 1 - Math.pow(1 + r, -n);
  return (P * r) / denom;
}

export default function CalculCapaciteEmprunt() {
  const [incomeNet, setIncomeNet] = useState("2500");
  const [charges, setCharges] = useState("400");
  const [targetDebtRate, setTargetDebtRate] = useState("35");
  const [annualRate, setAnnualRate] = useState("3,8");
  const [years, setYears] = useState("20");
  const [insuranceMonthly, setInsuranceMonthly] = useState("0");

  const computed = useMemo(() => {
    const inc = clamp(parseNum(incomeNet), 0, 1_000_000);
    const chg = clamp(parseNum(charges), 0, 1_000_000);
    const debt = clamp(parseNum(targetDebtRate), 0, 60);
    const rate = clamp(parseNum(annualRate), 0, 30);
    const y = clamp(parseNum(years), 0, 40);
    const months = Math.round(y * 12);
    const ins = clamp(parseNum(insuranceMonthly), 0, 100_000);

    // mensualité max (tous crédits) selon taux d’endettement
    const mensualiteMaxAll = Math.max(0, inc * (debt / 100) - chg);

    // mensualité max dispo pour nouveau crédit, hors assurance
    const mensualiteMaxCredit = Math.max(0, mensualiteMaxAll - ins);

    // montant empruntable estimé: on inverse par recherche (binaire) sur P
    // P -> mensualité(P) ≈ mensualiteMaxCredit
    let low = 0;
    let high = 50_000_000;
    for (let i = 0; i < 40; i++) {
      const mid = (low + high) / 2;
      const m = calcMonthlyPayment(mid, rate, months);
      if (m > mensualiteMaxCredit) high = mid;
      else low = mid;
    }
    const principal = low;

    const monthlyNoIns = calcMonthlyPayment(principal, rate, months);
    const monthlyTotal = monthlyNoIns + ins;
    const totalPaidNoIns = monthlyNoIns * months;
    const totalIns = ins * months;
    const totalPaidWithIns = totalPaidNoIns + totalIns;
    const totalInterest = totalPaidNoIns - principal;

    // Endettement réel avec nouveau crédit (approx)
    const debtReal = inc > 0 ? ((chg + monthlyTotal) / inc) * 100 : 0;

    return {
      inc,
      chg,
      debt,
      rate,
      y,
      months,
      ins,
      mensualiteMaxAll,
      mensualiteMaxCredit,
      principal,
      monthlyNoIns,
      monthlyTotal,
      totalInterest,
      totalPaidNoIns,
      totalIns,
      totalPaidWithIns,
      debtReal,
    };
  }, [incomeNet, charges, targetDebtRate, annualRate, years, insuranceMonthly]);

  const canCompute = computed.inc > 0 && computed.months > 0;

  return (
    <div className="page">
      <div className="hero">
        <h1>Calcul capacité d’emprunt</h1>
        <p className="muted" style={{ margin: 0 }}>
          Estimation indicative basée sur tes revenus, charges et un taux
          d’endettement cible. Calcul 100% local.
        </p>
      </div>

      <div style={{ height: 16 }} />

      <div className="grid-2">
        {/* Entrées */}
        <div className="card">
          <h2 className="cardTitle">Entrées</h2>

          <label className="label">
            Revenu net mensuel (€)
            <input
              className="input"
              value={incomeNet}
              onChange={(e) => setIncomeNet(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <label className="label">
            Charges mensuelles actuelles (€)
            <input
              className="input"
              value={charges}
              onChange={(e) => setCharges(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <label className="label">
            Taux d’endettement max (%)
            <input
              className="input"
              value={targetDebtRate}
              onChange={(e) => setTargetDebtRate(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <div className="grid-2">
            <label className="label">
              Taux annuel (%)
              <input
                className="input"
                value={annualRate}
                onChange={(e) => setAnnualRate(e.target.value)}
                inputMode="decimal"
              />
            </label>

            <label className="label">
              Durée (années)
              <input
                className="input"
                value={years}
                onChange={(e) => setYears(e.target.value)}
                inputMode="numeric"
              />
            </label>
          </div>

          <label className="label">
            Assurance mensuelle (€) (option)
            <input
              className="input"
              value={insuranceMonthly}
              onChange={(e) => setInsuranceMonthly(e.target.value)}
              inputMode="decimal"
            />
          </label>

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginTop: 14,
            }}
          >
            <button
              className="btn btnSecondary"
              onClick={() => {
                setIncomeNet("2500");
                setCharges("400");
                setTargetDebtRate("35");
                setAnnualRate("3,8");
                setYears("20");
                setInsuranceMonthly("0");
              }}
            >
              Réinitialiser
            </button>
          </div>

          <div className="small" style={{ marginTop: 10 }}>
            L’endettement cible varie selon profil, banque, reste à vivre, etc.
            (ex : 35%).
          </div>
        </div>

        {/* Résultats */}
        <div className="card">
          <h2 className="cardTitle">Résultats</h2>

          {!canCompute ? (
            <p className="muted">Renseigne au moins ton revenu et la durée.</p>
          ) : (
            <>
              <ResultRow
                label="Mensualité max (tous crédits)"
                value={formatEUR(computed.mensualiteMaxAll)}
              />
              <ResultRow
                label="Mensualité max crédit (hors assurance)"
                value={formatEUR(computed.mensualiteMaxCredit)}
              />
              <ResultRow
                label="Mensualité totale (avec assurance)"
                value={formatEUR(computed.monthlyTotal)}
              />
              <ResultRow
                label="Montant empruntable estimé"
                value={formatEUR(computed.principal)}
              />

              <div style={{ height: 14 }} />

              <h3
                style={{
                  margin: "0 0 10px 0",
                  fontSize: 16,
                  letterSpacing: ".2px",
                }}
              >
                Détails
              </h3>

              <div
                className="card"
                style={{ padding: 12, background: "rgba(255,255,255,.06)" }}
              >
                <SmallLine
                  label="Hypothèse taux"
                  value={`${computed.rate} %`}
                />
                <SmallLine
                  label="Durée"
                  value={`${computed.y} ans (${computed.months} mois)`}
                />
                <SmallLine
                  label="Endettement cible"
                  value={`${computed.debt} %`}
                />
                <SmallLine
                  label="Endettement estimé (avec nouveau crédit)"
                  value={`${computed.debtReal.toFixed(1)} %`}
                />
                <SmallLine
                  label="Coût total crédit (hors assurance)"
                  value={formatEUR(computed.totalPaidNoIns)}
                />
                <SmallLine
                  label="Coût total assurance"
                  value={formatEUR(computed.totalIns)}
                />
                <SmallLine
                  label="Coût total (avec assurance)"
                  value={formatEUR(computed.totalPaidWithIns)}
                />
                <SmallLine
                  label="Intérêts estimés"
                  value={formatEUR(computed.totalInterest)}
                />
              </div>

              <div className="small" style={{ marginTop: 10 }}>
                Le montant empruntable est calculé pour respecter la mensualité
                max (hors assurance).
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultRow({ label, value }) {
  return (
    <div
      className="resultRow"
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        padding: "10px 0",
        borderBottom: "1px solid rgba(255,255,255,.10)",
      }}
    >
      <span className="muted">{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function SmallLine({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        padding: "6px 0",
      }}
    >
      <span className="muted" style={{ fontSize: 13 }}>
        {label}
      </span>
      <strong style={{ fontSize: 13 }}>{value}</strong>
    </div>
  );
}
